import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiCamera, FiSun, FiHome, FiArrowRight } from 'react-icons/fi';
import OtherOccasions from '../components/OtherOccasions';
import SEO from '../components/SEO';

const ShootingBookings = () => {
  return (
    <div data-testid="shooting-page" className="pt-20">
      <SEO
        title="Film & TV Shoot Villa Bookings in Lonavala | Royal Garden Villas"
        description="Cinematic villa locations in Lonavala for film shoots, TV ad commercials, music videos and OTT productions. Private estate, multiple villas, lawn shots, pool shots, valley views."
        path="/shooting"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Film & TV Shoots', url: '/shooting' },
        ]}
      />
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[360px] overflow-hidden bg-[#1A1A2E]">
        {/* Blurred background fill */}
        <img src="https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/09y0k1ne_shooting%20banner.PNG" alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-70" />
        {/* Full image centered */}
        <img src="https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/09y0k1ne_shooting%20banner.PNG" alt="Film shoot at Royal Garden Villas" className="relative h-full max-w-full mx-auto object-contain z-[1]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E]/80 via-transparent to-transparent z-[2]" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto z-[3]">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Film & TV Production</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Shoot at <span className="text-[#F57C20]">Royal Garden</span>
          </h1>
          <p className="text-white/60 font-light mt-3 max-w-lg text-sm">
            Premium shooting location in Lonavala for films, TV shows, music videos, brand campaigns, and pre-wedding shoots.
          </p>
        </div>
      </section>

      {/* Location Highlights */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Shoot <span className="text-[#F57C20]">Here?</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FiHome, title: 'Diverse Sets', desc: '3 unique villas with distinct interiors — luxury, rustic, and modern aesthetics in one compound.' },
              { icon: FiCamera, title: 'Natural Backdrops', desc: 'Lush gardens, private pools, mountain views, and open lawns perfect for outdoor sequences.' },
              { icon: FiSun, title: 'Golden Hour', desc: 'West-facing property with stunning sunset views ideal for magic-hour cinematography.' },
              { icon: FiCheck, title: 'Production-Friendly', desc: 'Ample parking, generator hookup points, and flexible timings for night shoots.' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-100">
                <item.icon className="w-6 h-6 text-[#F57C20] mb-4" />
                <h3 className="text-[#1A1A2E] text-sm font-semibold mb-2">{item.title}</h3>
                <p className="text-[#6B7280] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-20 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-white mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Location <span className="text-[#F57C20]">Gallery</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['/images/3bhk/2.jpg', '/images/4bhk/7.jpg', '/images/5bhk/3.jpg', '/images/12bhk/2.jpg',
              '/images/3bhk/4.jpg', '/images/4bhk/11.jpg', '/images/5bhk/5.jpg', '/images/12bhk/4.jpg'].map((img, i) => (
              <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden">
                <img src={img} alt={`Shooting location ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-light text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Shooting <span className="text-[#F57C20]">Rates</span></h2>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {[
                  { name: '3BHK Garden Escape', price: 'Rs.30,000 / day', cap: '6 pax' },
                  { name: '4BHK Garden Retreat', price: 'Rs.40,000 / day', cap: '8 pax' },
                  { name: '5BHK Garden Delight', price: 'Rs.50,000 / day', cap: '10 pax' },
                  { name: '12BHK Celebration Villa', price: 'Rs.1,00,000 / day', cap: '24 pax' },
                ].map((item, i) => (
                  <div key={i} className={`flex justify-between items-center px-6 py-4 ${i < 3 ? 'border-b border-gray-50' : ''}`}>
                    <div>
                      <span className="text-[#1A1A2E] text-sm font-medium block">{item.name}</span>
                      <span className="text-[#9CA3AF] text-xs">Up to {item.cap}</span>
                    </div>
                    <span className="text-[#F57C20] font-bold text-sm">{item.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#9CA3AF] text-xs mt-4">* Rates exclusive of electricity. Generator to be arranged by production team.</p>
            </div>

            <div>
              <h2 className="text-2xl font-light text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Shooting <span className="text-[#F57C20]">Rules</span></h2>
              <ul className="space-y-3">
                {[
                  "Generator must be arranged by production team",
                  "Electricity billed separately based on meter reading",
                  "Property damage deposit: Rs.20,000 (refundable)",
                  "Shoot timings must be specified and strictly adhered to",
                  "No loud noise after 10 PM (residential area)",
                  "Clean-up is the responsibility of the production team",
                  "Production company insurance details required in advance",
                  "No structural modifications without prior written consent"
                ].map((rule, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <FiCheck className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />
                    <span className="text-[#6B7280] text-sm">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Internal linking — other occasions */}
      <OtherOccasions currentPath="/shooting" heading="Also Perfect For" />

      {/* CTA */}
      <section className="py-16 border-t border-white/5 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="text-xl font-light text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Ready to Book a Shoot?</h3>
          <p className="text-white/40 font-light mb-8 text-sm">Contact us for availability and custom packages</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919820088292" className="bg-[#F57C20] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-all">
              Call +91 98200 88292
            </a>
            <Link to="/contact" className="border-2 border-white/20 text-white px-8 py-3 rounded-lg text-sm font-semibold hover:border-[#F57C20] hover:text-[#F57C20] transition-all flex items-center justify-center gap-2">
              Send Enquiry <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShootingBookings;
